import React, { useEffect, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { MultiSelectList } from 'src/domains/common/components/multi-select-list/multi-select-list';
import { Common, useCommon } from 'src/AppState/Common';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { SportsPickerState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/sportsbook-sections/sports-picker/SportsPickerState.state';
import { makeAutoObservable } from 'mobx';
import {
    checkIsArray,
    getFieldsFromCriteria,
    getFieldsFromUsageConditions,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { SportsPickerTreeType } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/sportsbook-sections/sports-picker/utils';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';

class SportsViewerState {
    public readonly sportsPickerState: SportsPickerState;

    public constructor(private readonly common: Common) {
        this.sportsPickerState = new SportsPickerState(this.common);
        this.sportsPickerState.setReadOnlyMode(true);

        makeAutoObservable(this);
    }

    public get sportsTreeForView(): Array<SportsPickerTreeType> {
        const filterTreeLeaves = (
            tree: SportsPickerTreeType[],
            condition: (node: SportsPickerTreeType) => boolean
        ): Array<SportsPickerTreeType> => {
            return tree
                .map((node) => {
                    if (node.children !== undefined && node.children.length > 0) {
                        const filteredChildren = filterTreeLeaves(node.children, condition);

                        if (filteredChildren.length > 0) {
                            return {
                                ...node,
                                children: filteredChildren,
                            };
                        } else {
                            return null;
                        }
                    } else {
                        return condition(node) ? node : null;
                    }
                })
                .filter((node): node is SportsPickerTreeType => node !== null);
        };
        return filterTreeLeaves(this.sportsPickerState.filteredNodes, (node) =>
            this.sportsPickerState.checked.includes(node.value)
        );
    }

    public setSelectedSports = async (campaignModel: CampaignModel, type: SportsViewerType): Promise<void> => {
        const criteria =
            type === 'action-conditions'
                ? getFieldsFromCriteria(campaignModel)
                : getFieldsFromUsageConditions(campaignModel);

        return await this.sportsPickerState.setInitValues({
            sportIds: checkIsArray(criteria?.['sport']) ?? [],
            competitionIds: checkIsArray(criteria?.['competition']) ?? [],
            eventIds: checkIsArray(criteria?.['event']) ?? [],
            marketIds: checkIsArray(criteria?.['market']) ?? [],
        });
    };
}

type SportsViewerType = 'action-conditions' | 'reward-conditions';

type SportsViewerPropsType = {
    campaignModel: CampaignModel;
    type: SportsViewerType;
};

export const SportsViewer = observer('SportsViewer', (props: SportsViewerPropsType): JSX.Element => {
    const common = useCommon();

    const [stateAutoMap] = useState(() => new AutoMap((_key) => new SportsViewerState(common)));
    const state = stateAutoMap.get(props.campaignModel.id);

    useEffect(() => {
        state
            .setSelectedSports(props.campaignModel, props.type)
            .then()
            .catch((e) => console.error(e));
    });

    const {
        showAllBadges,
        expanded,
        visibleChecked,
        keywordInputState,
        checked,
        isLoading,
        toggleShowAllBadges,
        handleCheck,
        handleExpand,
        setKeyword,
        handleRemoveChecked,
    } = state.sportsPickerState;

    return (
        <div className='flex flex-col gap-2 relative'>
            {isLoading && (
                <div className='absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center flex-auto gap-6 p-6 bg-white/75 font-tertiary flex-nowrap animate-pulse'>
                    <div className='w-8 h-8 border-4 border-dashed rounded-full animate-spin border-foreground' />
                </div>
            )}
            <MultiSelectList
                filteredNodes={state.sportsTreeForView}
                showAllBadges={showAllBadges}
                checked={checked}
                visibleChecked={visibleChecked}
                keywordInputState={keywordInputState}
                expanded={expanded}
                visibleCheckedWithLabels={[]}
                toggleShowAllBadges={toggleShowAllBadges}
                handleCheck={handleCheck}
                handleExpand={handleExpand}
                setKeyword={setKeyword}
                handleRemoveChecked={handleRemoveChecked}
                remaining={visibleChecked.remaining}
                remainingLabel='Items'
                noCascade
            />
        </div>
    );
});
